/**
 * Main App Component
 * Root component with routing and providers
 * Includes iOS-style page transitions for premium feel
 * 
 * @author Varun Pandey
 */

import { Routes, Route, useLocation, Navigate, matchPath, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { SafeAreaProvider } from './components/layout/SafeAreaProvider';
import { GlobalPullToRefresh } from './components/layout/GlobalPullToRefresh';
import { PersistentVideo } from './components/layout/PersistentVideo';
import { PublicRouteGuard } from './components/auth/PublicRouteGuard';
import { useAuthStore } from './stores/authStore';
import { useVideoStore } from './stores/videoStore';
import { getAuthCookies, hasValidAuthCookies } from './utils/cookies';
import { STORAGE_KEYS } from './storage/storageKeys';
import { SignupFlow } from './pages/auth/SignupFlow';
import { SignInPage } from './pages/auth/SignInPage';
import { ClaimProfilePage } from './pages/auth/ClaimProfilePage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { NotFoundPage } from './pages/errors/NotFoundPage';
import { ToastTestPage } from './pages/test/ToastTestPage';
import { TermsOfServicePage } from './pages/legal/TermsOfServicePage';
import { UserHome } from './pages/dashboard/UserHome';
import { AgencyHome } from './pages/dashboard/AgencyHome';
import { PublicMembershipPage } from './pages/public/PublicMembershipPage';
import { PublicComparePlansPage } from './pages/public/PublicComparePlansPage';
import { Toaster } from './components/ui/Toaster';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { InstagramBridgePage } from './pages/auth/InstagramBridgePage';
import { PreviewMyWebsitePage } from './pages/profile/PreviewMyWebsitePage';
import { YouTubeEmbedProxyPage } from './pages/embed/YouTubeEmbedProxyPage';
import { useRevenueCatSession } from '@/hooks/useRevenueCatSession';
import { useOneSignalSession } from '@/hooks/useOneSignalSession';
import { initializeOneSignal } from '@/services/oneSignal';
import apiClient from '@/api/client';
import { API_ENDPOINTS } from '@/api/endpoints';
import type { User } from '@/api/types';

const isEmbedRoute = (pathname: string) => pathname.startsWith('/embed/');

const Dashboard = () => <div className="p-8">Dashboard (To be implemented)</div>;

// iOS-optimized easing curve (typed as tuple for Framer Motion)
const iosEasing: [number, number, number, number] = [0.25, 0.1, 0.25, 1.0];
const iosExitEasing: [number, number, number, number] = [0.4, 0.0, 1, 1];

// Route transition variants - subtle iOS-style fade with slight movement
const routeVariants = {
  initial: {
    opacity: 0,
    y: 8,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: iosEasing,
    },
  },
  exit: {
    opacity: 0,
    y: -4,
    transition: {
      duration: 0.2,
      ease: iosExitEasing,
    },
  },
};

interface VerifySessionResponse {
  success: boolean;
  data: {
    authenticated: boolean;
    role?: string;
    active_artist_slug?: string;
  };
}

function normalizeSessionRole(role?: string): User['role'] {
  const normalized = role?.toLowerCase() ?? '';
  if (normalized === 'agency' || normalized === 'influencer' || normalized === 'artist') {
    return normalized;
  }
  return 'artist';
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const markAuthenticatedFromSession = useAuthStore(
    (state) => state.markAuthenticatedFromSession,
  );
  const hasAuthCookies = hasValidAuthCookies();
  const isBookingFillRoute = Boolean(
    matchPath('/jobs/booking-fill/:formId', location.pathname),
  );
  const needsSessionVerify =
    !isAuthenticated && !hasAuthCookies && !isBookingFillRoute;
  const [sessionVerifyResolved, setSessionVerifyResolved] = useState(!needsSessionVerify);

  useEffect(() => {
    if (!isAuthenticated && (hasAuthCookies || isBookingFillRoute)) {
      const roleCookie = getAuthCookies().role?.toLowerCase() ?? '';
      const role =
        roleCookie === 'agency' || roleCookie === 'influencer' || roleCookie === 'artist'
          ? roleCookie
          : 'artist';
      markAuthenticatedFromSession(role);
    }
  }, [isAuthenticated, hasAuthCookies, isBookingFillRoute, markAuthenticatedFromSession]);

  useEffect(() => {
    if (isAuthenticated) {
      setSessionVerifyResolved(true);
      return;
    }
    if (hasAuthCookies || isBookingFillRoute) {
      setSessionVerifyResolved(true);
      return;
    }

    let cancelled = false;
    setSessionVerifyResolved(false);

    const verifySession = async () => {
      try {
        const response = await apiClient.get<VerifySessionResponse>(
          API_ENDPOINTS.AUTH.VERIFY_SESSION,
          { withCredentials: true },
        );
        if (cancelled) return;

        if (response.data.authenticated) {
          markAuthenticatedFromSession(normalizeSessionRole(response.data.data.role));
        }
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 401) {
          // no-op — fall through to login redirect
        }
      } finally {
        if (!cancelled) setSessionVerifyResolved(true);
      }
    };

    void verifySession();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, hasAuthCookies, isBookingFillRoute, markAuthenticatedFromSession]);

  // Allow booking deep links to render first so cookie/session hydration can complete
  // before we force a login redirect on fresh-tab opens.
  if (isAuthenticated || hasAuthCookies || isBookingFillRoute) {
    return <>{children}</>;
  }

  if (!sessionVerifyResolved) {
    return null;
  }

  if (!isAuthenticated) {
    const shouldSuppressRedirectToast =
      typeof window !== 'undefined' &&
      Boolean(sessionStorage.getItem(STORAGE_KEYS.SUPPRESS_LOGIN_REDIRECT_TOAST));
    return (
      <Navigate
        to="/login"
        replace
        state={
          shouldSuppressRedirectToast
            ? undefined
            : { message: 'Your session has expired. Please sign in again.' }
        }
      />
    );
  }
  return <>{children}</>;
}

/**
 * Animated Routes Component
 * Wraps all routes with AnimatePresence for smooth page transitions
 * This ensures any future routes automatically get transitions
 * Note: UserHome routes don't animate here as they handle their own content animations
 */
function ChatNotificationBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    void import("@/services/oneSignal/chatNotificationHandler").then(
      ({ setupChatNotificationHandlers, teardownChatNotificationHandlers }) => {
        void setupChatNotificationHandlers(navigate);
      },
    );

    return () => {
      void import("@/services/oneSignal/chatNotificationHandler").then(
        ({ teardownChatNotificationHandlers }) => {
          void teardownChatNotificationHandlers();
        },
      );
    };
  }, [navigate]);

  return null;
}

function AnimatedRoutes() {
  const location = useLocation();

  const isStaticEmbedRoute = isEmbedRoute(location.pathname);

  // Check if current route is a UserHome route (should not animate at page level)
  const isUserHomeRoute =
    isStaticEmbedRoute ||
    location.pathname === '/user-home' ||
    [
      '/home',
      '/assistant',
      '/profile',
      '/jobs',
      '/wallet',
      '/membership',
      '/compare-plans',
      '/statistics',
      '/settings',
      '/contact-us',
      '/faq',
      '/express-booking-consent-form',
      '/express-booking',
    ].includes(
      location.pathname,
    ) ||
    location.pathname.startsWith('/profile/') ||
    location.pathname.startsWith('/jobs/');

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={isUserHomeRoute ? 'user-home' : location.pathname}
        initial={isUserHomeRoute ? undefined : 'initial'}
        animate={isUserHomeRoute ? undefined : 'animate'}
        exit={isUserHomeRoute ? undefined : 'exit'}
        variants={routeVariants}
        style={{
          // GPU acceleration for smooth iOS animations
          willChange: 'opacity, transform',
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden',
          minHeight: 'var(--app-height, 100dvh)',
          // Prevent white flash during page transition (exit/enter)
          backgroundColor: '#0B0B0D',
        }}
      >
        <Routes location={location}>
          {/* Public routes - redirect to home if already authenticated */}
          <Route path="/" element={<PublicRouteGuard pageType="signup"><SignupFlow /></PublicRouteGuard>} />
          <Route path="/login" element={<PublicRouteGuard pageType="login"><SignInPage /></PublicRouteGuard>} />
          <Route path="/forgot-password" element={<PublicRouteGuard pageType="login"><ForgotPasswordPage /></PublicRouteGuard>} />
          <Route path="/signup" element={<PublicRouteGuard pageType="signup"><SignupFlow /></PublicRouteGuard>} />
          <Route path="/claim-profile" element={<ClaimProfilePage />} />
          <Route path="/test/toast" element={<ToastTestPage />} />
          <Route path="/instagram-bridge" element={<InstagramBridgePage />} />
          <Route path="/instagram/callback" element={<InstagramBridgePage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />

          {/* Public shareable pricing pages – no auth, logo-only layout */}
          <Route path="/pricing" element={<PublicMembershipPage />} />
          <Route path="/pricing/compare" element={<PublicComparePlansPage />} />

          {/* Public YouTube embed proxy for native iOS gallery iframes */}
          <Route path="/embed/youtube/:videoId" element={<YouTubeEmbedProxyPage />} />

          {/* Protected routes */}
          <Route
            path="/user-home"
            element={
              <ProtectedRoute>
                <UserHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <UserHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assistant"
            element={
              <ProtectedRoute>
                <UserHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/*"
            element={
              <ProtectedRoute>
                <UserHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/chat/:bookingId"
            element={
              <ProtectedRoute>
                <UserHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/booking-fill/:formId"
            element={
              <ProtectedRoute>
                <UserHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs/:id"
            element={
              <ProtectedRoute>
                <UserHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs"
            element={
              <ProtectedRoute>
                <UserHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wallet"
            element={
              <ProtectedRoute>
                <UserHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/membership"
            element={
              <ProtectedRoute>
                <UserHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/compare-plans"
            element={
              <ProtectedRoute>
                <UserHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/statistics"
            element={
              <ProtectedRoute>
                <UserHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <UserHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contact-us"
            element={
              <ProtectedRoute>
                <UserHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/faq"
            element={
              <ProtectedRoute>
                <UserHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agency-home"
            element={
              <ProtectedRoute>
                <AgencyHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/preview-my-website"
            element={
              <ProtectedRoute>
                <PreviewMyWebsitePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/express-booking-consent-form"
            element={
              <ProtectedRoute>
                <UserHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/express-booking"
            element={
              <ProtectedRoute>
                <UserHome />
              </ProtectedRoute>
            }
          />

          {/* 404 Not Found */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <ChatNotificationBridge />
      </motion.div>
    </AnimatePresence>
  );
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID ?? '';

function App() {
  useRevenueCatSession();
  useOneSignalSession();
  const location = useLocation();

  useEffect(() => {
    void useAuthStore.getState().hydrateAccessTokenFromSecureStorage();
  }, []);

  // Initialize OneSignal SDK at startup; permission is requested after artist login.
  useEffect(() => {
    void (async () => {
      try {
        await initializeOneSignal();
      } catch (error) {
        console.error('[OneSignal] Startup initialization failed:', error);
      }
    })();
  }, []);
  const isSignupRoute = location.pathname === '/' || location.pathname === '/signup';
  const isEmbedPage = isEmbedRoute(location.pathname);
  const isPublicPricingRoute =
    location.pathname === '/pricing' ||
    location.pathname === '/pricing/compare' ||
    location.pathname === '/terms';
  const setShowVideo = useVideoStore((s) => s.setShowVideo);

  // Ensure landing page (/) shows the video.
  // Signup flow controls its own visibility per-step.
  useEffect(() => {
    if (location.pathname === '/') {
      setShowVideo(true);
      return () => setShowVideo(false);
    }
  }, [location.pathname, setShowVideo]);

  // Keep document scroll locked globally, except on public pricing pages
  // that are designed to scroll as full-document pages.
  useEffect(() => {
    const prevOverflowY = document.body.style.overflowY;
    const prevOverscrollBehavior = document.body.style.overscrollBehavior;

    if (isPublicPricingRoute) {
      document.body.style.overflowY = 'auto';
      document.body.style.overscrollBehavior = 'auto';
    } else {
      document.body.style.overflowY = 'hidden';
      document.body.style.overscrollBehavior = 'none';
    }

    return () => {
      document.body.style.overflowY = prevOverflowY;
      document.body.style.overscrollBehavior = prevOverscrollBehavior;
    };
  }, [isPublicPricingRoute]);

  // Shell height for min-h-screen / h-screen. Use the larger of layout vs visual
  // viewport height so when the keyboard only shrinks the visual viewport
  // (interactive-widget=overlays-content), the layout does not collapse and show
  // a black band above the keyboard.
  useEffect(() => {
    const setViewportHeightVar = () => {
      const inner = window.innerHeight;
      const vvH = window.visualViewport?.height ?? inner;
      const h = Math.max(inner, vvH);
      document.documentElement.style.setProperty('--app-height', `${h}px`);
    };

    setViewportHeightVar();

    window.addEventListener('resize', setViewportHeightVar);
    window.addEventListener('orientationchange', setViewportHeightVar);
    window.visualViewport?.addEventListener('resize', setViewportHeightVar);
    window.visualViewport?.addEventListener('scroll', setViewportHeightVar);

    return () => {
      window.removeEventListener('resize', setViewportHeightVar);
      window.removeEventListener('orientationchange', setViewportHeightVar);
      window.visualViewport?.removeEventListener('resize', setViewportHeightVar);
      window.visualViewport?.removeEventListener('scroll', setViewportHeightVar);
    };
  }, []);

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <SafeAreaProvider>
        <Toaster />
        <GlobalPullToRefresh />
        {/* Persistent video that doesn't reset on navigation */}
        {!isEmbedPage && <PersistentVideo />}
        <div
          className={`min-h-screen overflow-x-hidden ${
            !isSignupRoute && !isEmbedPage ? 'bg-[#0B0B0D]' : ''
          }`}
        >
          <AnimatedRoutes />
        </div>
      </SafeAreaProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
